import { createFileRoute } from "@tanstack/react-router";
import { useState, useRef, useCallback, useEffect, useMemo, useId } from "react";
import { useDashboardContext } from "@/components/dashboard/DashboardContext";
import {
	DashboardLayout,
} from "@/components/dashboard/DashboardLayout";
import { useSession } from "@/hooks/auth-hooks";
import { db } from "@/lib/instantdb";

export const Route = createFileRoute("/dashboard/generations")({
	component: GenerationsPage,
});

type Generation = {
	id: string;
	createdAt: string;
	generationType: string;
	model: string | null;
	outputUrl: string | null;
	prompt: string | null;
	status: string;
	updatedAt: string;
	error?: string | null;
};

type GenerationEntity = {
	createdAt: Date | string | number;
	error?: string | null;
	generationType: string;
	id: string;
	model?: string | null;
	outputUrl?: string | null;
	prompt?: string | null;
	status: string;
	updatedAt: Date | string | number;
};

const PAGE_SIZE = 12;
type FilterType = "all" | "image" | "video" | "audio";

// ── Audio player (adapted from brandreel AudioPreview + AudioVisualizer) ──────

const BIN_COUNT = 96;
const IDLE_LEVEL = 0.05;

type WindowWithWebkitAudio = Window & {
	webkitAudioContext?: typeof AudioContext;
};

function resampleBins(values: number[], targetCount: number) {
	if (values.length === targetCount) return values;
	const sliceSize = values.length / targetCount;
	return Array.from({ length: targetCount }, (_, index) => {
		const start = Math.floor(index * sliceSize);
		const end = Math.max(start + 1, Math.floor((index + 1) * sliceSize));
		let total = 0;
		for (let i = start; i < end; i++) total += values[i] ?? IDLE_LEVEL;
		return total / (end - start);
	});
}

function buildSmoothPath(points: Array<{ x: number; y: number }>) {
	if (points.length === 0) return "";
	if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
	let path = `M ${points[0].x} ${points[0].y}`;
	for (let i = 1; i < points.length - 1; i++) {
		const cur = points[i];
		const next = points[i + 1];
		const midX = (cur.x + next.x) / 2;
		const midY = (cur.y + next.y) / 2;
		path += ` Q ${cur.x} ${cur.y} ${midX} ${midY}`;
	}
	const last = points[points.length - 1];
	return path + ` T ${last.x} ${last.y}`;
}

function useAudioSpectrum(audioElement: HTMLAudioElement | null) {
	const createIdleBins = () => Array.from({ length: BIN_COUNT }, () => IDLE_LEVEL);
	const [bins, setBins] = useState<number[]>(createIdleBins);
	const binsRef = useRef<number[]>(createIdleBins());
	const [isPlaying, setIsPlaying] = useState(false);

	useEffect(() => { binsRef.current = bins; }, [bins]);

	useEffect(() => {
		if (!audioElement || typeof window === "undefined") return;
		let rafId = 0;
		let disposed = false;
		let audioContext: AudioContext | null = null;
		let analyser: AnalyserNode | null = null;
		let sourceNode: MediaElementAudioSourceNode | null = null;
		let frequencyData: Uint8Array<ArrayBuffer> | null = null;

		const updateBins = (targetBins: number[]) => {
			const nextBins = targetBins.map((target, i) => {
				const prev = binsRef.current[i] ?? IDLE_LEVEL;
				return prev * 0.74 + Math.max(IDLE_LEVEL, target) * 0.26;
			});
			binsRef.current = nextBins;
			setBins(nextBins);
		};

		const fallbackFrame = () => {
			if (disposed) return;
			const t = performance.now() / 1000;
			const playing = !audioElement.paused;
			setIsPlaying(playing);
			const base = playing ? 0.14 : IDLE_LEVEL;
			updateBins(Array.from({ length: BIN_COUNT }, (_, i) => {
				const osc = Math.sin(t * 4.1 + i * 0.37) * 0.18 + Math.sin(t * 2.3 + i * 0.19) * 0.1;
				return base + Math.max(-0.1, osc);
			}));
			rafId = window.requestAnimationFrame(fallbackFrame);
		};

		const audioFrame = () => {
			if (disposed || !analyser || !frequencyData) return;
			analyser.getByteFrequencyData(frequencyData);
			const bucketSize = Math.max(1, Math.floor(frequencyData.length / BIN_COUNT));
			const playing = !audioElement.paused;
			setIsPlaying(playing);
			updateBins(Array.from({ length: BIN_COUNT }, (_, i) => {
				const start = i * bucketSize;
				const end = Math.min(frequencyData!.length, start + bucketSize);
				if (start >= end || !playing) return IDLE_LEVEL;
				let total = 0;
				for (let j = start; j < end; j++) total += frequencyData![j] ?? 0;
				return total / (end - start) / 255;
			}));
			rafId = window.requestAnimationFrame(audioFrame);
		};

		const AudioContextClass = window.AudioContext || (window as WindowWithWebkitAudio).webkitAudioContext;
		try {
			if (!AudioContextClass) throw new Error("AudioContext unavailable");
			audioContext = new AudioContextClass();
			analyser = audioContext.createAnalyser();
			analyser.fftSize = 256;
			analyser.smoothingTimeConstant = 0.7;
			sourceNode = audioContext.createMediaElementSource(audioElement);
			sourceNode.connect(analyser);
			analyser.connect(audioContext.destination);
			frequencyData = new Uint8Array(new ArrayBuffer(analyser.frequencyBinCount));
			const handlePlay = () => {
				if (audioContext?.state === "suspended") void audioContext.resume().catch(() => {});
			};
			audioElement.addEventListener("play", handlePlay);
			if (!audioElement.paused) handlePlay();
			rafId = window.requestAnimationFrame(audioFrame);
			return () => {
				disposed = true;
				audioElement.removeEventListener("play", handlePlay);
				window.cancelAnimationFrame(rafId);
				sourceNode?.disconnect();
				analyser?.disconnect();
				void audioContext?.close().catch(() => {});
			};
		} catch {
			rafId = window.requestAnimationFrame(fallbackFrame);
			return () => { disposed = true; window.cancelAnimationFrame(rafId); };
		}
	}, [audioElement]);

	const energy = useMemo(() => {
		const sample = resampleBins(bins, 24);
		return sample.reduce((sum, v) => sum + v, 0) / sample.length;
	}, [bins]);

	return { bins, isPlaying, energy };
}

function WaveVisualizer({ bins, energy, isPlaying }: { bins: number[]; energy: number; isPlaying: boolean }) {
	const gradientId = `af-wave-${useId().replace(/:/g, "")}`;
	const points = useMemo(() => {
		const levels = resampleBins(bins, 84);
		return levels.map((level, i) => {
			const x = (i / (levels.length - 1)) * 100;
			const cd = Math.abs(x - 50);
			const envelope = Math.exp(-((cd * cd) / (2 * 18 * 18)));
			const osc = Math.sin((i / (levels.length - 1)) * Math.PI * 12.5);
			const amplitude = envelope * (2.8 + level * 14 + energy * 12) * (isPlaying ? 1 : 0.45);
			return { x, y: 50 - osc * amplitude };
		});
	}, [bins, energy, isPlaying]);

	const path = useMemo(() => buildSmoothPath(points), [points]);

	return (
		<svg viewBox="0 0 100 100" className="size-full" preserveAspectRatio="none" aria-hidden="true">
			<defs>
				<linearGradient id={gradientId} x1="0" y1="0" x2="100" y2="0" gradientUnits="userSpaceOnUse">
					<stop offset="0" stopColor="#0f0f10" stopOpacity="0" />
					<stop offset="0.12" stopColor="#6B3FF2" stopOpacity="0.4" />
					<stop offset="0.5" stopColor="#6B3FF2" stopOpacity="1" />
					<stop offset="0.88" stopColor="#6B3FF2" stopOpacity="0.4" />
					<stop offset="1" stopColor="#0f0f10" stopOpacity="0" />
				</linearGradient>
			</defs>
			<path d={path} fill="none" stroke={`url(#${gradientId})`} strokeWidth="1.15" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

function AudioPlayer({ audioUrl }: { audioUrl: string }) {
	const audioRef = useRef<HTMLAudioElement>(null);
	const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const { bins, energy, isPlaying: spectrumPlaying } = useAudioSpectrum(audioElement);

	const setAudioNode = useCallback((node: HTMLAudioElement | null) => {
		audioRef.current = node;
		setAudioElement(node);
		setIsPlaying(false);
	}, []);

	const togglePlay = () => {
		const audio = audioRef.current;
		if (!audio) return;
		if (isPlaying) { audio.pause(); return; }
		setIsPlaying(true);
		void audio.play().catch(() => setIsPlaying(false));
	};

	return (
		<div className="relative size-full overflow-hidden" style={{ backgroundColor: "#080808" }}>
			<WaveVisualizer bins={bins} energy={energy} isPlaying={spectrumPlaying} />
			<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
			<button
				type="button"
				onClick={togglePlay}
				aria-label={isPlaying ? "Pause" : "Play"}
				className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex size-12 items-center justify-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
				style={{ border: "1px solid #6B3FF2", backgroundColor: "rgba(0,0,0,0.5)" }}
				onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(234,40,4,0.12)"; }}
				onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(0,0,0,0.5)"; }}
				onFocus={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(234,40,4,0.12)"; }}
				onBlur={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(0,0,0,0.5)"; }}
			>
				<span aria-hidden="true" className="material-symbols-outlined text-white" style={{ fontSize: "22px", fontVariationSettings: "'FILL' 1", marginLeft: isPlaying ? "0" : "2px" }}>
					{isPlaying ? "pause" : "play_arrow"}
				</span>
			</button>
			{/* biome-ignore lint/a11y/useMediaCaption: preview audio clips do not have caption tracks */}
			<audio key={audioUrl} ref={setAudioNode} src={audioUrl} onEnded={() => setIsPlaying(false)} onPause={() => setIsPlaying(false)} onPlay={() => setIsPlaying(true)} />
		</div>
	);
}

// ─────────────────────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
	const config: Record<
		string,
		{ label: string; className: string; icon: string; spin?: boolean }
	> = {
		completed: {
			label: "COMPLETED",
			className:
				"bg-[rgba(43,154,102,0.12)] text-[#2b9a66] border-[#2b9a66]",
			icon: "check_circle",
		},
		processing: {
			label: "PROCESSING",
			className:
				"bg-[rgba(234,40,4,0.1)] text-[#6B3FF2] border-[#6B3FF2]",
			icon: "progress_activity",
			spin: true,
		},
			pending: {
				label: "PENDING",
				className:
					"bg-[rgba(119,117,117,0.1)] text-[var(--km-on-surface-muted)] border-[rgba(255,255,255,0.08)]",
				icon: "schedule",
			},
		failed: {
			label: "FAILED",
			className:
				"bg-[rgba(255,110,132,0.1)] text-[#ff6e84] border-[#ff6e84]",
			icon: "cancel",
		},
	};

	const { label, className, icon, spin } = config[status] ?? config.pending;

	return (
		<span
			className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[9px] font-bold tracking-[0.1em] uppercase ${className}`}
		>
			<span
				aria-hidden="true"
				className={`material-symbols-outlined ${spin ? "animate-spin" : ""}`}
				style={{ fontSize: "10px", fontVariationSettings: "'FILL' 1" }}
			>
				{icon}
			</span>
			{label}
		</span>
	);
}

function TypeIcon({ type }: { type: string }) {
	const icon =
		type === "video" ? "movie" : type === "audio" ? "graphic_eq" : "image";
	return (
		<span
			aria-hidden="true"
			className="material-symbols-outlined"
			style={{ fontSize: "32px", color: "#888888" }}
		>
			{icon}
		</span>
	);
}


function GenerationCard({
	generation,
	onRefetch,
}: {
	generation: Generation;
	onRefetch: () => void;
}) {
	const { status, generationType, outputUrl, prompt, model, createdAt } =
		generation;

	const isCompleted = status === "completed";
	const isProcessing = status === "processing" || status === "pending";
	const isFailed = status === "failed";

	function handleCopyMcp() {
		const snippet = `// MCP generation snippet\nconst result = await mcp.getGeneration("${generation.id}");`;
		navigator.clipboard.writeText(snippet);
	}

	return (
		<div
			className="group rounded-none overflow-hidden flex flex-col transition-all duration-500"
			style={{
				backgroundColor: "var(--km-surface-low)",
				border: "1px solid rgba(255,255,255,0.08)",
			}}
			onMouseEnter={(e) => {
				(e.currentTarget as HTMLElement).style.borderColor = "#6B3FF2";
				(e.currentTarget as HTMLElement).style.borderTopColor = "#6B3FF2";
			}}
			onMouseLeave={(e) => {
				(e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.08)";
				(e.currentTarget as HTMLElement).style.borderTopColor = "rgba(255,255,255,0.08)";
			}}
		>
			{/* Thumbnail */}
			<div className="relative aspect-video overflow-hidden" style={{ backgroundColor: "var(--km-surface)" }}>
				{outputUrl && generationType === "image" && (
					<>
						<img
							src={outputUrl}
							alt={prompt ?? "Generated image"}
							className="w-full h-full object-cover"
						/>
						<div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
					</>
				)}

				{generationType === "audio" && outputUrl && (
					<AudioPlayer audioUrl={outputUrl} />
				)}

				{generationType === "audio" && !outputUrl && (
					<div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: "var(--km-surface)" }}>
						<span aria-hidden="true" className="material-symbols-outlined text-[#6B3FF2]" style={{ fontSize: "40px", fontVariationSettings: "'FILL' 1" }}>audio_file</span>
					</div>
				)}

				{outputUrl && generationType === "video" && (
					<>
						<video
							src={outputUrl}
							title={prompt ?? "Generated video"}
							controls
							className="w-full h-full object-cover"
							muted
							preload="metadata"
						/>
						<div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
						<div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
							<span
								aria-hidden="true"
								className="material-symbols-outlined text-white"
								style={{ fontSize: "48px" }}
							>
								play_circle
							</span>
						</div>
					</>
				)}

				{isProcessing && (
					<div
						className="w-full h-full flex flex-col items-center justify-center gap-4"
						style={{
							background:
								"linear-gradient(135deg, var(--km-surface) 0%, rgba(234,40,4,0.02) 100%)",
						}}
					>
						<div className="size-10 rounded-full border-2 border-t-transparent border-[#6B3FF2] animate-spin" />
						<span
							className="text-[10px] tracking-[0.1em] text-[#6B3FF2] animate-pulse uppercase"
							style={{ fontFamily: "inherit" }}
						>
							Synthesizing...
						</span>
					</div>
				)}

				{isFailed && (
					<div
						className="w-full h-full flex flex-col items-center justify-center gap-2"
						style={{ backgroundColor: "rgba(127,29,29,0.1)" }}
					>
						<span
							aria-hidden="true"
							className="material-symbols-outlined"
							style={{ fontSize: "48px", color: "rgba(239,68,68,0.3)" }}
						>
							warning
						</span>
					</div>
				)}

				{isCompleted && !outputUrl && (
					<div className="w-full h-full flex items-center justify-center">
						<TypeIcon type={generationType} />
					</div>
				)}

				{/* Status badge */}
				<div className="absolute top-3 left-3">
					<StatusBadge status={status} />
				</div>

				{/* Bottom overlay: model + date for completed media */}
				{isCompleted &&
					outputUrl &&
					(generationType === "image" || generationType === "video") && (
						<div className="absolute bottom-3 left-3 right-3 flex justify-between items-end">
							{model && (
								<span
									className="text-[10px] uppercase tracking-wide"
									style={{
										fontFamily: "inherit",
										color: "rgba(255,255,255,0.5)",
									}}
								>
									{model}
								</span>
							)}
							<span
								className="text-[10px] ml-auto"
								style={{
									fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
									color: "rgba(255,255,255,0.55)",
								}}
							>
								{new Date(createdAt).toLocaleDateString()}
							</span>
						</div>
					)}
			</div>

			{/* Card body */}
			<div
				className="p-5 space-y-4 flex-1 flex flex-col"
				style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
			>
				{/* Prompt */}
				<div
					className="p-3 rounded-none"
					style={{
						backgroundColor: "var(--km-surface)",
						border: "1px solid rgba(255,255,255,0.08)",
						borderLeft: isCompleted ? "3px solid #6B3FF2" : isFailed ? "3px solid #7f1d1d" : "3px solid rgba(255,255,255,0.07)",
					}}
				>
					<p
						className="line-clamp-2 text-xs font-mono leading-relaxed"
						style={{
							color: isCompleted ? "#6B3FF2" : isFailed ? "var(--km-on-surface-muted)" : "var(--km-on-surface-muted)",
							fontStyle: isCompleted ? "normal" : "italic",
						}}
					>
						{prompt ?? "No prompt provided"}
					</p>
				</div>

				{/* Meta */}
				<div
					className="grid grid-cols-3 gap-2 pb-3 text-[9px] uppercase tracking-wide font-bold"
					style={{
						fontFamily: "inherit",
						borderBottom: "1px solid rgba(255,255,255,0.08)",
					}}
				>
					<div>
						<span className="block" style={{ color: "var(--km-on-surface-muted)" }}>
							Type
						</span>
						<span
							className="inline-flex items-center px-1.5 py-0.5"
							style={{
								backgroundColor: generationType === "image" ? "rgba(234,40,4,0.08)" : generationType === "video" ? "rgba(234,40,4,0.10)" : "rgba(43,154,102,0.12)",
								color: generationType === "image" ? "#6B3FF2" : generationType === "video" ? "#6B3FF2" : "#2b9a66",
								border: generationType === "image" ? "1px solid #6B3FF2" : generationType === "video" ? "1px solid #6B3FF2" : "1px solid #2b9a66",
							}}
						>{generationType}</span>
					</div>
					<div>
						<span className="block" style={{ color: "var(--km-on-surface-muted)" }}>
							Date
						</span>
						<span style={{ color: "var(--km-on-surface)" }}>
							{new Date(createdAt).toLocaleDateString("en-US", {
								month: "short",
								day: "numeric",
							})}
						</span>
					</div>
					<div className="text-right">
						<span className="block" style={{ color: "var(--km-on-surface-muted)" }}>
							Model
						</span>
						<span className="truncate block" style={{ color: "var(--km-on-surface)" }}>
							{model ?? "-"}
						</span>
					</div>
				</div>

				{/* Actions */}
				{(isCompleted && outputUrl) || isProcessing || isFailed ? (
					<div className="flex gap-2 mt-auto pt-2">
						{isCompleted && outputUrl && generationType === "audio" && (
							<a
								href={outputUrl}
								download
								aria-label="Download audio"
								className="flex-1 flex items-center justify-center gap-1.5 py-2 text-[10px] font-bold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
								style={{ backgroundColor: "rgba(255,255,255,0.04)", color: "#d4d4d8", fontFamily: "inherit" }}
								onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#2c2c2c"; }}
								onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onFocus={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#2c2c2c"; }}
								onBlur={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
							>
								<span aria-hidden="true" className="material-symbols-outlined" style={{ fontSize: "14px" }}>download</span>
							</a>
						)}

						{isCompleted && outputUrl && (generationType === "image" || generationType === "video") && (
							<a
								href={outputUrl}
								download
								aria-label="Download file"
								className="flex-1 flex items-center justify-center gap-1.5 py-2 text-[10px] font-bold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
								style={{ backgroundColor: "rgba(255,255,255,0.04)", color: "#d4d4d8", fontFamily: "inherit" }}
								onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#2c2c2c"; }}
								onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onFocus={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#2c2c2c"; }}
								onBlur={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
							>
								<span aria-hidden="true" className="material-symbols-outlined" style={{ fontSize: "14px" }}>download</span>
							</a>
						)}

						{isCompleted && (generationType === "image" || generationType === "video") && (
							<button
								type="button"
								onClick={handleCopyMcp}
								aria-label="Copy MCP snippet"
								className="flex-1 flex items-center justify-center gap-1.5 py-2 text-[10px] font-bold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
								style={{ backgroundColor: "rgba(255,255,255,0.04)", color: "#d4d4d8", fontFamily: "inherit" }}
								onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#2c2c2c"; }}
								onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
								onFocus={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#2c2c2c"; }}
								onBlur={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#201f1f"; }}
							>
								<span aria-hidden="true" className="material-symbols-outlined" style={{ fontSize: "14px" }}>code</span>
							</button>
						)}

						{isProcessing && (
							<div className="flex gap-2 flex-1 opacity-50 cursor-not-allowed">
								<button type="button" disabled aria-label="Download (unavailable while processing)" className="flex-1 flex items-center justify-center gap-1.5 py-2 text-[10px] font-bold uppercase tracking-wide" style={{ backgroundColor: "var(--km-surface-high)", color: "var(--km-on-surface-muted)", fontFamily: "inherit" }}>
									<span aria-hidden="true" className="material-symbols-outlined" style={{ fontSize: "14px" }}>download</span>
								</button>
								<button type="button" disabled aria-label="Copy MCP snippet (unavailable while processing)" className="flex-1 flex items-center justify-center gap-1.5 py-2 text-[10px] font-bold uppercase tracking-wide" style={{ backgroundColor: "var(--km-surface-high)", color: "var(--km-on-surface-muted)", fontFamily: "inherit" }}>
									<span aria-hidden="true" className="material-symbols-outlined" style={{ fontSize: "14px" }}>code</span>
								</button>
							</div>
						)}

						{isFailed && (
							<button
								type="button"
								onClick={onRefetch}
								className="flex-1 flex items-center justify-center gap-1.5 py-2 text-[10px] font-bold uppercase tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
								style={{ backgroundColor: "rgba(255,110,132,0.08)", color: "#ff6e84", fontFamily: "inherit" }}
								onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,110,132,0.14)"; }}
								onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,110,132,0.08)"; }}
								onFocus={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,110,132,0.14)"; }}
								onBlur={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "rgba(255,110,132,0.08)"; }}
							>
								<span aria-hidden="true" className="material-symbols-outlined" style={{ fontSize: "14px" }}>refresh</span>
								Retry
							</button>
						)}
					</div>
				) : null}
			</div>
		</div>
	);
}

function SkeletonCard() {
	return (
			<div
				className="animate-pulse rounded-none overflow-hidden"
				style={{
					backgroundColor: "var(--km-surface-low)",
					border: "1px solid rgba(255,255,255,0.08)",
				}}
			>
				<div className="aspect-video" style={{ backgroundColor: "var(--km-surface)" }} />
				<div className="p-5 space-y-3">
					<div
						className="h-2.5 w-3/4"
						style={{ backgroundColor: "var(--km-surface-high)" }}
					/>
					<div
						className="h-2.5 w-1/2"
						style={{ backgroundColor: "var(--km-surface-high)" }}
					/>
				<div className="flex gap-2 pt-2">
					<div
						className="h-7 rounded-none w-20"
						style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
					/>
					<div
						className="h-7 rounded-none w-16"
						style={{ backgroundColor: "rgba(255,255,255,0.04)" }}
					/>
				</div>
			</div>
		</div>
	);
}

const filterTabs: { label: string; value: FilterType }[] = [
	{ label: "All Assets", value: "all" },
	{ label: "Images", value: "image" },
	{ label: "Video", value: "video" },
	{ label: "Audio", value: "audio" },
];

function GenerationsPage() {
	const { user } = useSession();
	const { workspace } = useDashboardContext();
	const [activeFilter, setActiveFilter] = useState<FilterType>("all");
	const [search, setSearch] = useState("");
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

	const { data, isLoading } = db.useQuery(
		user?.id && workspace?.id
			? {
					workspaces: {
						$: { where: { id: workspace.id } },
						generations: {},
					},
				}
			: null,
	);

	const allGenerations: Generation[] = (
		data?.workspaces?.[0]?.generations ?? []
	)
		.map((generation: GenerationEntity) => ({
			createdAt: new Date(generation.createdAt).toISOString(),
			error: generation.error ?? null,
			generationType: generation.generationType,
			id: generation.id,
			model: generation.model ?? null,
			outputUrl: generation.outputUrl ?? null,
			prompt: generation.prompt ?? null,
			status: generation.status,
			updatedAt: new Date(generation.updatedAt).toISOString(),
		}))
		.sort(
			(a, b) =>
				new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
		);

	const filtered = allGenerations.filter((g) => {
		const matchesType =
			activeFilter === "all" || g.generationType === activeFilter;
		const matchesSearch =
			!search || (g.prompt ?? "").toLowerCase().includes(search.toLowerCase());
		return matchesType && matchesSearch;
	});

	const visible = filtered.slice(0, visibleCount);
	const hasMore = filtered.length > visibleCount;

	return (
		<DashboardLayout>
			{/* Page header */}
				<div className="px-8 pt-8 pb-6 flex flex-col md:flex-row md:items-end justify-between gap-6">
					<div className="space-y-2">
						<p
							className="text-[11px] font-bold uppercase tracking-[0.1em]"
							style={{ fontFamily: "inherit", color: "#6B3FF2" }}
						>
							Dashboard
						</p>
						<h1
							className="text-5xl font-semibold tracking-tighter"
							style={{ fontFamily: "'Geist', -apple-system, sans-serif", color: "var(--km-on-surface)" }}
						>
							Recent Generations
						</h1>
						<p
							className="text-xs uppercase tracking-wide font-bold"
							style={{
								fontFamily: "inherit",
								color: "#888888",
							}}
						>
							Archive of kinetic synthesis operations
						</p>
					</div>
					{/* Filter tabs inline with header on desktop */}
					<div
						className="flex items-center gap-1 rounded-none p-1.5 shrink-0"
						style={{
							backgroundColor: "var(--km-surface-low)",
							border: "1px solid rgba(255,255,255,0.08)",
						}}
					>
						<div
							className="flex items-center px-2 gap-1.5"
							style={{ color: "var(--km-on-surface-muted)" }}
						>
							<span
								aria-hidden="true"
								className="material-symbols-outlined"
								style={{ fontSize: "14px" }}
							>
								search
							</span>
							<span
								className="text-[10px] font-bold uppercase tracking-wide"
								style={{ fontFamily: "inherit" }}
							>
								Filter:
							</span>
						</div>
						{filterTabs.map((tab) => (
							<button
								key={tab.value}
								type="button"
								onClick={() => setActiveFilter(tab.value)}
								aria-pressed={activeFilter === tab.value}
								className={`px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.1em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60 ${
									activeFilter === tab.value
										? ""
										: ""
								}`}
								style={{
									fontFamily: "inherit",
									backgroundColor: activeFilter === tab.value ? "var(--km-surface-high)" : "transparent",
									color: activeFilter === tab.value ? "#6B3FF2" : "var(--km-on-surface-muted)",
									border: activeFilter === tab.value ? "1px solid #6B3FF2" : "1px solid transparent",
								}}
							>
								{tab.label}
							</button>
						))}
					</div>
				</div>

				{/* Search + Refresh bar */}
				<div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 px-8 pb-6">
					{/* Search */}
					<div className="relative flex-1 min-w-0 group">
						<span
							aria-hidden="true"
							className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none group-focus-within:text-[#6B3FF2] transition-colors"
							style={{ fontSize: "14px", color: "var(--km-on-surface-muted)" }}
						>
							search
						</span>
						<input
							type="text"
							aria-label="Search generations"
							placeholder="Search projects, model names, or generation IDs..."
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="w-full pl-10 pr-4 py-2.5 text-xs focus:outline-none focus-visible:ring-1 focus-visible:ring-[#0072f5]/40 focus:border-[#6B3FF2]/30 transition-all"
							style={{
								backgroundColor: "var(--km-surface-low)",
								border: "1px solid rgba(255,255,255,0.08)",
								color: "var(--km-on-surface)",
							}}
						/>
					</div>

					{/* Refresh */}
					<button
						type="button"
						onClick={() => window.location.reload()}
						className="flex items-center gap-2 px-4 py-2.5 bg-[#6B3FF2] text-[#ffffff] text-[10px] font-semibold hover:opacity-90 transition-all active:scale-95 shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
						style={{
							fontFamily: "inherit",
							letterSpacing: "0.1em",
						}}
					>
						<span
							aria-hidden="true"
							className="material-symbols-outlined"
							style={{ fontSize: "14px" }}
						>
							refresh
						</span>
						REFRESH ARCHIVE
					</button>
				</div>

				{/* Grid */}
				{isLoading ? (
					<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 px-8 pb-8">
						{Array.from({ length: 6 }).map((_, i) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: static skeleton list
							<SkeletonCard key={i} />
						))}
					</div>
				) : filtered.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-24 px-8 text-center gap-4">
						<div
							className="w-16 h-16 flex items-center justify-center rounded-none"
							style={{
								backgroundColor: "rgba(234,40,4,0.06)",
								border: "1px solid #6B3FF2",
							}}
						>
							<span
								aria-hidden="true"
								className="material-symbols-outlined"
								style={{ fontSize: "28px", color: "#6B3FF2" }}
							>
								image
							</span>
						</div>
						<div>
							<p
								className="text-sm font-semibold"
								style={{ fontFamily: "'Geist', -apple-system, sans-serif", color: "var(--km-on-surface)" }}
							>
								{allGenerations.length === 0
									? "No generations yet"
									: "No results found"}
							</p>
							<p
								className="text-xs mt-1"
								style={{
									fontFamily: "inherit",
									color: "var(--km-on-surface-muted)",
								}}
							>
								{allGenerations.length === 0
									? "Ask your assistant to generate an image, video, or audio file."
									: "No generations match your current filters."}
							</p>
						</div>
					</div>
				) : (
					<>
						<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 px-8 pb-8">
							{visible.map((generation) => (
								<GenerationCard
									key={generation.id}
									generation={generation}
									onRefetch={() => window.location.reload()}
								/>
							))}
						</div>
						{hasMore && (
							<div className="flex justify-center pb-12">
								<button
									type="button"
									onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
									className="px-8 py-3 text-xs font-bold uppercase tracking-wide transition-all hover:opacity-90"
									style={{
										fontFamily: "inherit",
										backgroundColor: "var(--km-surface-low)",
										border: "1px solid rgba(255,255,255,0.08)",
										color: "var(--km-on-surface-muted)",
									}}
									onMouseEnter={(e) => {
										(e.currentTarget as HTMLElement).style.borderColor =
											"rgba(234,40,4,0.35)";
										(e.currentTarget as HTMLElement).style.color = "#6B3FF2";
									}}
									onMouseLeave={(e) => {
										(e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.08)";
										(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)";
									}}
									onFocus={(e) => {
										(e.currentTarget as HTMLElement).style.borderColor =
											"rgba(234,40,4,0.35)";
										(e.currentTarget as HTMLElement).style.color = "#6B3FF2";
									}}
									onBlur={(e) => {
										(e.currentTarget as HTMLElement).style.borderColor = "rgba(255,255,255,0.08)";
										(e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)";
									}}
								>
									Load Archive Segment{" "}
									{Math.floor(visibleCount / PAGE_SIZE) + 1}
								</button>
							</div>
						)}
					</>
				)}
		</DashboardLayout>
	);
}
